import config from '@/config';
import delay from '@/helpers/delay';
import { logger } from '@/helpers/logger';
import useForm from '@/hooks/useForm/useForm';
import { useState } from 'react';
import * as yup from 'yup';
import ButtonSubmit from '../../ButtonSubmit/ButtonSubmit';
import type { ButtonSubmitStateType } from '../../ButtonSubmit/interfaces';
import { FormFieldType } from '../../FormField/consts';
import FormField from '../../FormField/FormField';
import FormFieldLength from '../../FormField/FormFieldLength';
import Icon from '../../Icon/Icon';
import styles from './BenefitsSuggestionCardForm.module.scss';
import type { BenefitsSuggestionCardFormProps } from './types';

const schema = yup.object({
  name: yup.string().required('Name ist erforderlich'),
  link: yup.string().url('Bitte geben Sie eine gültige URL ein'),
  note: yup.string(),
});

const initialState: yup.InferType<typeof schema> = {
  name: '',
  link: '',
  note: '',
};

export const BenefitsSuggestionCardForm = ({
  email,
  onClose,
}: BenefitsSuggestionCardFormProps) => {
  const [submitState, setSubmitState] =
    useState<ButtonSubmitStateType>('initial');

  const {
    formState,
    handleSubmit,
    handleChange,
    handleBlur,
    isFormValid,
    getErrorByFieldName,
  } = useForm(
    async (data) => {
      setSubmitState('pending');

      if (!email) {
        setSubmitState('fail');
        logger.error('BenefitsSuggestionCardForm: User email is not available');
        await delay(2000);
        setSubmitState('initial');
        return;
      }

      try {
        const response = await fetch(
          new URL('/api/benefits/suggestion', config.apiBaseUrl),
          {
            method: 'POST',
            body: JSON.stringify({ ...data, email: email }),
          }
        );

        if (!response.ok) {
          throw new Error(response.statusText);
        }
      } catch (error) {
        setSubmitState('fail');
        await delay(2000);
        setSubmitState('initial');
        return;
      }
      setSubmitState('success');
    },
    schema,
    initialState
  );

  return (
    <div className={styles.base}>
      <Icon
        className={styles.closeIcon}
        variant="action/close"
        size="iconSize24"
        onClick={onClose}
        aria-label="Schließen"
      />

      <h2 className={styles.title}>Partner vorschlagen</h2>
      <p className={styles.description}>
        Machen Sie die enercity Vorteilwelt noch besser! Ihnen fehlt ein
        Partnerangebot? Schlagen Sie uns gerne ein Unternehmen vor:
      </p>

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formFields}>
          <FormField
            name="name"
            label="Name des Unternehmens"
            value={formState.name ?? ''}
            onChange={handleChange}
            onBlur={handleBlur}
            errorMessageHasRelativePosition
            errorMessage={getErrorByFieldName('name')}
            prefixBoxIcon="user/business-building"
          />
          <FormField
            name="link"
            label="Link (optional)"
            value={formState.link ?? ''}
            onChange={handleChange}
            onBlur={handleBlur}
            errorMessage={getErrorByFieldName('link')}
            errorMessageHasRelativePosition
            prefixBoxIcon="action/link"
          />
          <div>
            <FormField
              name="note"
              type={FormFieldType.Textarea}
              label="Anmerkungen (optional)"
              value={formState.note ?? ''}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={500}
              restrictMaxLength
            />
            <FormFieldLength
              maxLength={500}
              show={true}
              value={formState.note}
              isOnGradient={false}
              className={styles.formFieldLength}
            />
          </div>
        </div>

        <ButtonSubmit state={submitState} inactive={!isFormValid}>
          Jetzt vorschlagen
        </ButtonSubmit>
      </form>
    </div>
  );
};
